"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "../ui/OptimizedImage";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiFetchAuthed } from "../../lib/api";
import { queryKeys, ENV_PREFIX } from "../../lib/query-keys";
import { scheduleIdle } from "../../lib/idle";
import { buildEventPath } from "../../lib/routes";
import { useMyEventIds } from "../../hooks/useMyEventIds";
import { EventPlaceholderCover } from "./EventPlaceholderCover";
import { AttendingBadge } from "./AttendingBadge";

import type { Category } from "./CategoriesSection";

type EventSummary = {
    id: string;
    eventId?: string;
    shortId?: string | null;
    title: string;
    date: string;
    imageUrl: string | null;
    hostName: string;
    category?: string | null;
    tags?: string[] | null;
};

type OnboardingStatus = {
    hasHostProfile: boolean;
};


const formatDate = (value: string) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Date TBA";

    return date.toLocaleDateString("en-GB", {
        month: "short",
        day: "numeric",
        timeZone: "Asia/Kolkata",
    });
};

// isUpcoming filter removed — API already filters with upcoming=true,
// and Date.now() in useMemo causes hydration mismatch with ISR

interface TrendingEventsProps {
    category?: Category;
    tag?: string;
    searchQuery?: string;
    minPriceQuery?: string;
    maxPriceQuery?: string;
    initialData?: unknown[];
}

export function TrendingEvents({ category, tag, searchQuery, minPriceQuery, maxPriceQuery, initialData }: TrendingEventsProps) {
    const categoryId = category?.id && category.id !== "all" ? category.id : "";
    const queryClient = useQueryClient();
    const { isSignedIn, getToken } = useAuth();
    const scrollerRef = useRef<HTMLDivElement | null>(null);
    const [showGhostCard, setShowGhostCard] = useState(false);
    const myEventIds = useMyEventIds();
    const { data, isLoading } = useQuery({
        queryKey: queryKeys.events.trending({ limit: 6, upcoming: true, trending: true, categoryId, tag, search: searchQuery, minPrice: minPriceQuery, maxPrice: maxPriceQuery }),
        queryFn: () => {
            const params = new URLSearchParams({
                limit: "6",
                upcoming: "true",
                trending: "true",
            });
            if (categoryId) {
                params.set("categoryId", categoryId);
            }
            if (tag) {
                params.set("tag", tag);
            }
            if (searchQuery) {
                params.set("search", searchQuery);
            }
            if (minPriceQuery) {
                params.set("minPrice", minPriceQuery);
            }
            if (maxPriceQuery) {
                params.set("maxPrice", maxPriceQuery);
            }
            return apiFetch<EventSummary[]>(`events?${params.toString()}`);
        },
        initialData: !categoryId && !tag && !searchQuery && !minPriceQuery && !maxPriceQuery
            ? (initialData as EventSummary[] | undefined)
            : undefined,
        staleTime: 60_000,
    });
    const { data: onboardingStatus } = useQuery({
        queryKey: queryKeys.onboarding.status(),
        queryFn: () =>
            apiFetchAuthed<OnboardingStatus>("onboarding/status", {}, () => getToken()),
        enabled: isSignedIn,
        staleTime: 60_000,
    });

    const events = useMemo(
        () =>
            (data ?? []).map((event) => ({
                id: event.id,
                eventId: event.eventId ?? event.id,
                shortId: event.shortId,
                title: event.title,
                date: formatDate(event.date),
                image: event.imageUrl ?? null,
                category: event.category ?? event.tags?.[0] ?? null,
            })),
        [data],
    );
    const isHost = isSignedIn ? Boolean(onboardingStatus?.hasHostProfile) : null;
    const hostCta = useMemo(() => {
        if (!isSignedIn) {
            return { label: "Start hosting", href: `/auth?redirect=${encodeURIComponent("/onboarding?start=hostType")}` };
        }
        if (isHost === false) {
            return { label: "Become a host", href: "/onboarding?start=hostType" };
        }
        return { label: "Host one now", href: "/dashboard" };
    }, [isSignedIn, isHost]);

    useEffect(() => {
        if (events.length === 0) {
            return;
        }
        return scheduleIdle(() => {
            events.forEach((event) => {
                queryClient.prefetchQuery({
                    queryKey: [ENV_PREFIX, "event", event.id],
                    queryFn: () => apiFetch<unknown>(`events/${event.id}`),
                    staleTime: 30_000,
                });
            });
        });
    }, [events, queryClient]);
    useEffect(() => {
        const element = scrollerRef.current;
        if (!element) {
            return;
        }
        const cardWidth = 176;
        const gap = 12;
        const measure = () => {
            if (events.length === 0) {
                setShowGhostCard(false);
                return;
            }
            const width = element.clientWidth;
            const contentWidth =
                events.length * cardWidth + Math.max(events.length - 1, 0) * gap;
            setShowGhostCard(contentWidth < width - 4);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [events.length]);
    const handleScroll = (direction: "left" | "right") => {
        const element = scrollerRef.current;
        if (!element) {
            return;
        }
        const amount = Math.max(200, Math.floor(element.clientWidth * 0.7));
        element.scrollBy({
            left: direction === "left" ? -amount : amount,
            behavior: "smooth",
        });
    };

    if (isLoading) {
        return (
            <section className="px-4 py-6 md:px-6" aria-busy="true" aria-label="Loading trending events">
                <div className="mx-auto max-w-6xl">
                    <h2 className="mb-4 text-sm font-semibold text-[var(--hopp-text)]">
                        Trending
                    </h2>
                    <div className="flex gap-3 overflow-hidden">
                        {[1, 2, 3].map((i) => (
                            <div
                                key={i}
                                className="h-32 w-44 flex-shrink-0 animate-pulse rounded-xl bg-[var(--hopp-panel)]"
                            />
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (events.length === 0) {
        if (!categoryId) {
            return null;
        }
        return (
            <section className="px-4 py-6 md:px-6">
                <div className="mx-auto max-w-6xl">
                    <h2 className="mb-4 text-sm font-semibold text-[var(--hopp-text)]">
                        Trending
                    </h2>
                    <div className="rounded-2xl bg-[var(--hopp-panel)] px-6 py-8 text-center">
                        <p className="text-sm font-semibold text-[var(--hopp-text)]">
                            No events in {category?.name ?? "this category"}.
                        </p>
                        <p className="mt-2 text-xs text-[var(--hopp-muted)]">
                            Be the first to host one in this scene.
                        </p>
                        <Link
                            href={hostCta.href}
                            className="mt-4 inline-flex items-center justify-center rounded-full border border-white/20 bg-white/10 px-5 py-2 text-xs font-semibold text-[#EDEDED] backdrop-blur-md transition shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] hover:bg-white/15 hover:border-white/30"
                        >
                            {hostCta.label}
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="px-4 py-6 md:px-6">
            <div className="mx-auto max-w-6xl">
                {/* Section Header */}
                <h2 className="mb-4 text-sm font-semibold text-[var(--hopp-text)]">
                    Trending
                </h2>

                <div className="relative">
                    {/* Horizontal Scroll Container */}
                    <div
                        ref={scrollerRef}
                        tabIndex={0}
                        role="region"
                        aria-label="Trending events carousel"
                        onKeyDown={(e) => {
                            if (e.key === "ArrowLeft") handleScroll("left");
                            if (e.key === "ArrowRight") handleScroll("right");
                        }}
                        className="scrollbar-hide flex gap-3 overflow-x-auto px-2 pb-2 sm:px-0 snap-x snap-mandatory focus:outline-none focus-visible:ring-1 focus-visible:ring-white/20"
                    >
                        {events.map((event) => (
                            <Link
                                key={event.id}
                                href={buildEventPath(event.shortId ?? event.id, event.title)}
                                className="flex-shrink-0 snap-start"
                            >
                                <div
                                    className="group relative h-32 w-44 overflow-hidden rounded-xl transition-transform duration-150 ease-out hover:-translate-y-0.5"
                                >
                                    {/* Background Image */}
                                    {event.image ? (
                                        <Image
                                            src={event.image}
                                            alt={event.title}
                                            fill
                                            sizes="176px"
                                            className="object-cover transition-transform duration-150 group-hover:scale-105"
                                        />
                                    ) : (
                                        <EventPlaceholderCover
                                            title={event.title}
                                            category={event.category}
                                            size="sm"
                                        />
                                    )}

                                    {/* Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                                    {/* You're going pill */}
                                    {myEventIds.has(event.eventId) ? <AttendingBadge /> : null}

                                    {/* Content */}
                                    <div className="absolute inset-x-0 bottom-0 p-3">
                                        <p className="text-[9px] font-medium text-[#EDEDED]/80">
                                            {event.date}
                                        </p>
                                        <h3 className="line-clamp-2 min-h-[2.5em] text-xs font-semibold leading-snug text-white">
                                            {event.title}
                                        </h3>
                                    </div>
                                </div>
                            </Link>
                        ))}
                        {showGhostCard ? (
                            <Link
                                href={hostCta.href}
                                className="flex-shrink-0 snap-start"
                                aria-label={hostCta.label}
                            >
                                <div
                                    className="group relative h-32 w-44 overflow-hidden rounded-xl border border-dotted border-[var(--hopp-panel-strong)] bg-transparent transition-transform duration-150 ease-out hover:-translate-y-0.5"
                                >
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="flex h-12 w-12 items-center justify-center rounded-full border border-dotted border-[var(--hopp-panel-strong)] text-3xl text-[var(--hopp-text)]">
                                            +
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ) : null}
                    </div>
                    {events.length > 1 && !showGhostCard ? (
                        <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-1">
                            <button
                                type="button"
                                onClick={() => handleScroll("left")}
                                className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/70 transition hover:text-white"
                                aria-label="Scroll trending events left"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M15 6l-6 6 6 6" />
                                </svg>
                            </button>
                            <button
                                type="button"
                                onClick={() => handleScroll("right")}
                                className="pointer-events-auto flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40 text-white/70 transition hover:text-white"
                                aria-label="Scroll trending events right"
                            >
                                <svg
                                    viewBox="0 0 24 24"
                                    className="h-4 w-4"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                >
                                    <path d="M9 6l6 6-6 6" />
                                </svg>
                            </button>
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
}
